import type { Dispatch, SetStateAction } from 'react';
import { expectedGuardianDistrictName } from './teacher-district';
import {
	getTeacherCategoryOptions,
	isScientificSupervisorCategory,
	isSmallOlympicVariant,
} from './teacher-registration-categories';

export type TeacherPrefill = {
	personalTitle?: string;
	academicDegree?: string;
	firstName?: string;
	secondName?: string;
	lastName?: string;
	phone?: string;
	category?: string;
	districtName?: string;
	city?: string;
	street?: string;
	zipCode?: string;
	building?: string;
	apartment?: string;
	affiliation?: string;
	schoolRspo?: number | string;
};

type SchoolRow = {
	rspo: number;
	name: string;
	city: string;
	street?: string;
	building?: string;
	zipCode?: string;
	districtID?: number | string;
	district_id?: number;
};

type DistrictRow = { id: number; name: string };

type FormInfoShape = {
	personalTitle: string;
	academicDegree: string;
	firstName: string;
	secondName: string;
	lastName: string;
	category: string;
	affiliation: string;
	students: string;
	email: string;
	phone: string;
	city: string;
	street: string;
	zipCode: string;
	building: string;
	apartment: string;
	districtId: string;
	password: string;
	passwordConfirm: string;
	noSchoolFacility: boolean;
	schoolRspo: string;
};

const norm = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

/** Porównanie ulic — bez prefiksów ul./pl., bez względu na wielkość liter. */
export function normalizeStreetForMatch(street: string): string {
	return norm(street)
		.replace(/^ul\.?\s+/, '')
		.replace(/^pl\.?\s+/, '')
		.replace(/^os\.?\s+/, '')
		.replace(/^al\.?\s+/, '')
		.trim();
}

export function streetsMatchForSchool(streetA: string, streetB: string): boolean {
	const a = normalizeStreetForMatch(streetA);
	const b = normalizeStreetForMatch(streetB);
	if (a === '' && b === '') {
		return true;
	}
	if (a === '' || b === '') {
		return false;
	}
	return a === b;
}

export function schoolDisplayLabel(school: SchoolRow): string {
	return `${school.city.toUpperCase()}, ${school.name.toUpperCase()}`;
}

export function mapPrefillCategory(category?: string): string {
	const options = getTeacherCategoryOptions();
	const c = (category || '').trim();
	if (!c) {
		return '';
	}
	if (c === options.teacher || c === options.scientificSupervisor) {
		return c;
	}
	if (/opiekun/i.test(c)) {
		return options.scientificSupervisor;
	}
	if (/nauczyciel|koordynator/i.test(c)) {
		return options.teacher;
	}
	return '';
}

/** Dopasowanie szkoły z poprzedniej edycji po RSPO lub adresie (miasto + ulica + budynek). */
export function matchSchoolFromPrefill(
	schools: SchoolRow[] | undefined,
	prefill: TeacherPrefill,
): SchoolRow | undefined {
	if (!schools?.length) {
		return undefined;
	}
	const rspo = Number(prefill.schoolRspo || 0);
	if (rspo > 0) {
		const byRspo = schools.find((s) => Number(s.rspo) === rspo);
		if (byRspo) {
			return byRspo;
		}
	}
	const city = norm(prefill.city || '');
	const building = norm(prefill.building || '');
	if (!city || !building) {
		return undefined;
	}
	return schools.find(
		(s) =>
			norm(s.city) === city &&
			norm(s.building || '') === building &&
			streetsMatchForSchool(prefill.street || '', s.street || ''),
	);
}

function resolveDistrict(
	prefill: TeacherPrefill,
	districts: DistrictRow[],
	matchedSchool?: SchoolRow,
): DistrictRow | undefined {
	const districtNames = districts.map((d) => d.name);
	let targetName = prefill.districtName || '';

	const schoolDistrictId = matchedSchool?.districtID ?? matchedSchool?.district_id;
	if (schoolDistrictId) {
		const fromSchool = districts.find((d) => d.id === Number(schoolDistrictId));
		if (fromSchool) {
			targetName = fromSchool.name;
		}
	}

	const zip = prefill.zipCode || '';
	const city = matchedSchool?.city || prefill.city || '';
	const expected = expectedGuardianDistrictName(city, zip, districtNames, isSmallOlympicVariant());
	if (expected) {
		targetName = expected;
	} else if (targetName) {
		const legacy = ['Warszawa', 'Mazowsze', 'mazowieckie'];
		if (legacy.includes(targetName) && zip) {
			const mazExpected = expectedGuardianDistrictName(city, zip, districtNames, isSmallOlympicVariant());
			if (mazExpected) {
				targetName = mazExpected;
			}
		}
	}

	return districts.find((d) => d.name === targetName);
}

export type PrefillApplyResult = {
	matchedSchool?: SchoolRow;
	schoolFromCatalog: boolean;
	affiliationHint: string;
	/** Stan formularza po prefill — do synchronizacji walidacji bez przechodzenia przez handlery. */
	appliedForm: FormInfoShape;
	appliedHelper: { district: string; affiliation: string; rodo: string; consent: string };
	hasSecondName: boolean;
};

/** Wypełnia formularz nauczyciela danymi z poprzedniej edycji (bez listy uczniów). */
export function applyTeacherPrefill<T extends FormInfoShape>(
	prefill: TeacherPrefill,
	districts: DistrictRow[] | undefined,
	schools: SchoolRow[] | undefined,
	setFormInfo: Dispatch<SetStateAction<T>>,
	setHelperInputInfo: Dispatch<
		SetStateAction<{ district: string; affiliation: string; rodo: string; consent: string }>
	>,
	setIsSecondName: (value: boolean) => void,
): PrefillApplyResult {
	const emptyForm: FormInfoShape = {
		personalTitle: '',
		academicDegree: '',
		firstName: '',
		secondName: '',
		lastName: '',
		category: '',
		affiliation: '',
		schoolRspo: '',
		students: '',
		email: '',
		phone: '',
		city: '',
		street: '',
		zipCode: '',
		building: '',
		apartment: '',
		districtId: '',
		password: '',
		passwordConfirm: '',
		noSchoolFacility: false,
	};
	const emptyHelper = { district: '', affiliation: '', rodo: '', consent: '' };
	const empty: PrefillApplyResult = {
		schoolFromCatalog: false,
		affiliationHint: '',
		appliedForm: emptyForm,
		appliedHelper: emptyHelper,
		hasSecondName: false,
	};
	if (!prefill || !districts) {
		return empty;
	}

	const matchedSchool = matchSchoolFromPrefill(schools, prefill);
	const district = resolveDistrict(prefill, districts, matchedSchool);
	const mappedCategory = mapPrefillCategory(prefill.category);
	const noSchool = isScientificSupervisorCategory(mappedCategory) && !matchedSchool;
	const affiliationHint = !matchedSchool && prefill.city ? prefill.city.toUpperCase() : '';

	const appliedForm: FormInfoShape = {
		...emptyForm,
		personalTitle: prefill.personalTitle || '',
		academicDegree: prefill.academicDegree || '',
		firstName: prefill.firstName || '',
		secondName: prefill.secondName || '',
		lastName: prefill.lastName || '',
		phone: prefill.phone || '',
		category: mappedCategory || '',
		city: matchedSchool?.city || prefill.city || '',
		street: matchedSchool?.street || prefill.street || '',
		zipCode: matchedSchool?.zipCode || prefill.zipCode || '',
		building: matchedSchool?.building || prefill.building || '',
		apartment: noSchool && prefill.apartment ? prefill.apartment : '',
		districtId: district ? String(district.id) : '',
		students: '',
		noSchoolFacility: noSchool,
		affiliation: matchedSchool ? schoolDisplayLabel(matchedSchool) : '',
		schoolRspo: matchedSchool ? String(matchedSchool.rspo) : '',
	};

	const appliedHelper = {
		district: district?.name || '',
		affiliation: matchedSchool ? schoolDisplayLabel(matchedSchool) : affiliationHint,
		rodo: '',
		consent: '',
	};

	setFormInfo((prev) => ({
		...prev,
		...appliedForm,
		email: prev.email || appliedForm.email,
		password: prev.password,
		passwordConfirm: prev.passwordConfirm,
	}));

	const hasSecondName = !!prefill.secondName;
	if (hasSecondName) {
		setIsSecondName(true);
	}
	setHelperInputInfo((prev) => ({
		...prev,
		district: appliedHelper.district || prev.district,
		affiliation: appliedHelper.affiliation || prev.affiliation,
	}));

	return {
		matchedSchool,
		schoolFromCatalog: !!matchedSchool,
		affiliationHint,
		appliedForm,
		appliedHelper,
		hasSecondName,
	};
}
